import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useExperiments } from '../../hooks/useExperiment';
import { useServedRegion } from '../../hooks/useServedRegion';
import { getBackendApiUrl } from '../../config/backend';
import styles from './AuthDisabledBanner.module.css';
import MockRoleSwitcher from './MockRoleSwitcher';

// TRACI-1189: a build-time constant, so a deployed build (VITE_AUTH_DISABLED is never
// "true" there; vite.config.ts refuses it) folds this to false and drops the role switch.
const LOCAL_AUTH_OFF_BUILD = import.meta.env.VITE_AUTH_DISABLED === 'true';

/**
 * TRAC-478: Unified dev-info pill.
 *
 * Consolidates what used to be split across the floating auth-off banner and
 * the top-of-chat ExperimentBadge into a single corner pill so the chat area
 * above can be laid out with nothing pinned above it. Stakeholders felt the
 * top banners were distracting and, practically, the extra header elements
 * were interfering with the starter-prompt viewport math.
 *
 * Rendered only in the outer portal frame (App.tsx gates /embed) so there's
 * no duplicate when the portal iframes /embed. The outer frame reads auth
 * from its own AuthProvider, experiments from shared localStorage, and
 * region from /health — all the same data the iframe would see.
 *
 * Shows when EITHER auth is disabled OR there's experiment/region data to
 * surface.
 */

const AuthDisabledBanner = () => {
  const { isAuthDisabled } = useAuth();
  const { experiments, isOverride } = useExperiments();
  const { region, pinned } = useServedRegion();
  // Which agent fleet (v1/v2) the backend is routing to. Reflects the
  // AGENT_VERSION env default and the live Admin -> Settings override.
  const [agentVersion, setAgentVersion] = useState<string | null>(null);
  // TRAC-478: Persist minimized + corner state across reloads — stakeholders
  // found it annoying that the pill popped back open on every refresh.
  const [minimized, setMinimized] = useState<boolean>(() => {
    return localStorage.getItem('traci_dev_pill_minimized') === 'true';
  });
  const [corner, setCorner] = useState<'br' | 'bl' | 'tr' | 'tl'>(() => {
    const stored = localStorage.getItem('traci_dev_pill_corner');
    return (stored === 'br' || stored === 'bl' || stored === 'tr' || stored === 'tl') ? stored : 'br';
  });

  useEffect(() => {
    localStorage.setItem('traci_dev_pill_minimized', String(minimized));
  }, [minimized]);

  useEffect(() => {
    localStorage.setItem('traci_dev_pill_corner', corner);
  }, [corner]);

  // Poll the effective agent version (public endpoint) so the pill shows
  // AGENT: V1/V2 and tracks live admin toggles.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${getBackendApiUrl()}/api/settings/agent-version`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data?.version) setAgentVersion(String(data.version));
        }
      } catch {
        // ignore — the indicator just won't render
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Dedupe experiment keys (session parser uses lowercase, override parser uses
  // uppercase — merge can produce both { a: 2, A: 2 }).
  const experimentKeys = Object.keys(experiments);
  const dedupedExperiments: Record<string, number> = {};
  for (const [f, v] of Object.entries(experiments)) {
    dedupedExperiments[f.toUpperCase()] = v;
  }
  const experimentCode = Object.entries(dedupedExperiments)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([f, v]) => `${f.toLowerCase()}${v}`)
    .join(' ');

  const hasExperiments = experimentKeys.length > 0;
  const showRegion = Boolean(region);
  const showDevMeta = hasExperiments || showRegion;

  if (!isAuthDisabled && !showDevMeta) return null;

  const cornerClass = styles[`corner_${corner}`] || '';
  const bannerClass = [
    styles.banner,
    isAuthDisabled ? styles.authOff : styles.neutral,
    minimized ? styles.minimized : '',
    cornerClass,
  ].filter(Boolean).join(' ');

  const icon = (
    <svg
      className={styles.icon}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {isAuthDisabled ? (
        <>
          <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V8a7 7 0 0 0-14 0v4a6.9 6.9 0 0 0 .31 2" />
          <path d="M2 16l10 6 10-6" />
          <path d="M2 12l10 6 10-6" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12" y2="16" />
        </>
      )}
    </svg>
  );

  if (minimized) {
    return (
      <div
        className={bannerClass}
        data-testid="auth-disabled-banner"
        onClick={() => setMinimized(false)}
        title="Click to expand"
      >
        {icon}
      </div>
    );
  }

  return (
    <div
      className={bannerClass}
      data-testid="auth-disabled-banner"
    >
      <div className={`${styles.row} ${styles.headerRow}`}>
        <div className={styles.headerLeft}>
          {icon}
          {isAuthDisabled && <span>AUTH OFF</span>}
        </div>
        <div className={styles.headerRight}>
          <button
            className={styles.minimizeBtn}
            onClick={() => setCorner(c => ({ br: 'bl', bl: 'tl', tl: 'tr', tr: 'br' } as const)[c])}
            title="Move corner"
          >
            &#x2922;
          </button>
          <button className={styles.minimizeBtn} onClick={() => setMinimized(true)} title="Minimize">
            &minus;
          </button>
        </div>
      </div>
      {LOCAL_AUTH_OFF_BUILD && isAuthDisabled && (
        <div className={styles.row}>
          <MockRoleSwitcher />
        </div>
      )}
      {hasExperiments && (
        <div className={styles.row}>
          <span className={`${styles.devMetaSection} ${isOverride ? styles.override : ''}`}>
            EXP: {experimentCode}{isOverride ? ' (override)' : ''}
          </span>
        </div>
      )}
      {showRegion && (
        <div className={styles.row}>
          <span className={styles.devMetaSection}>
            REGION: {region}
            {pinned !== 'auto' && (
              <span className={styles.pinned} title={`Pinned to ${pinned} via Dev Settings`}>
                📌
              </span>
            )}
          </span>
        </div>
      )}
      {agentVersion && (
        <div className={styles.row}>
          <span className={styles.devMetaSection} title="Agent fleet the backend is routing to (env default + Admin override)">
            AGENT: {agentVersion.toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
};

export default AuthDisabledBanner;
